// @ts-check
// Editor do bot (/service/bots/:botId?node=&tab=editor|settings; contrato de bots, seção 22.2), em modo foco (tela
// cheia, sem o menu lateral): as barras, o canvas horizontal dos fluxos com a árvore do bot, o painel lateral
// (catálogo de blocos, bloco, "Quando o bot começa", configurações e "Testar bot") e os diálogos. O rascunho fica na
// tela com desfazer e refazer (até 50 passos) e numa cópia no navegador (`appcrm.botDraft.<id>`, 500 ms); "Salvar"
// grava com a revisão esperada (409 EDIT_CONFLICT: recarregar ou copiar); "Salvar e ativar" grava e abre a revisão de
// ativação. Os números de agora atualizam a cada 30 s com a aba visível.
import { html } from 'htm/preact';
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { navigate, searchParamsState, updateSearchParams } from '../../app/router.js';
import { ApiError } from '../../data/api-client.js';
import {
  activateBot, createBot, deactivateBot, deleteBot, duplicateBot, getBot, getBotNodeCounts, previewBotActivation, saveBotDefinition, updateBotDetails,
} from '../../data/bots.js';
import { sessionState } from '../../data/metadata-store.js';
import { workspaceMembersState } from '../../data/workspace-members.js';
import { Button } from '../../ui/button.js';
import { EmptyState, Skeleton } from '../../ui/feedback.js';
import { useIsMobile, useMediaQuery } from '../../ui/hooks.js';
import { enqueueSnackbar, showErrorSnackbar, showSuccessSnackbar } from '../../ui/snackbar.js';
import { isTypingTarget } from '../../lib/keyboard-shortcuts.js';
import { FLOW_MOBILE_ZOOM, normalizeFlowZoom, readStoredFlowZoom, readStoredShowCounts, storeFlowZoom, storeShowCounts } from '../../lib/flow-zoom.js';
import { parseEmptyListFocus } from '../../lib/flow-canvas-keyboard.js';
import { BOT_ENTRY_FOCUS } from '../../lib/bot-canvas-keyboard.js';
import { botCatalogItem } from '../../lib/bot-catalog.js';
import {
  addStaticOption, botBranchSizes, botStepNumbers, canInsertBotNode, clearLocalBotDraft, createBotHistory, createBotNodeFromCatalog, droppedBranches,
  findBotNodeLocation, gotoNodesReferencing, insertBotNode, insertPointAfterBotTarget, insertPointKey, isSameBotDefinition, pushBotHistory, readLocalBotDraft,
  redoBotHistory, removeBotNode, removeStaticOption, shouldOfferLocalBotDraft, undoBotHistory, updateBotNode, writeLocalBotDraft,
} from '../../lib/bot-editor-state.js';
import { botBranchKeysOf, countBotNodes, indexBotTree, isBotSplitNode, variablesBefore } from '../../lib/bot-tree.js';
import { blockingBotProblems, botProblemsByNode, computeBotEditorProblems, freshBotServerProblems, mergeBotProblems } from '../../lib/bot-validation.js';
import { BOT_LIST_PATH, botEditorHref, botStatsHref } from '../../lib/bot-list.js';
import { MAXIMUM_BOT_NODES, MAXIMUM_BOT_SPLIT_DEPTH } from '../../../../shared/bots/bot-limits.js';
import { createEmptyBotDefinition } from '../../../../shared/bots/bot-definition.js';
import { PageCard, PageHeader } from '../page/page-card.js';
import { FlowSidePanel } from '../flows/flow-side-panel.js';
import { BotActivationModal } from './bot-activation-modal.js';
import { BotCanvas } from './bot-canvas.js';
import { BotBlockCatalog } from './bot-catalog-panel.js';
import {
  BotConflictModal, BotDeleteSplitModal, BotDropBranchesConfirm, BotLeaveModal, BotLifecycleConfirm, BotNameDialog,
} from './bot-dialogs.js';
import { BotDraftBanner, BotEditorBars, readBotEditorTab } from './bot-editor-header.js';
import { BotTriggersPanel } from './bot-entry.js';
import { buildBotValidationContext, useBotLookupData, useBotSummaryLookups } from './bot-lookups.js';
import { BotNodePanel } from './bot-node-panel.js';
import { BotSettingsPanel } from './bot-settings-panel.js';
import { BotTestPanel } from './bot-test-panel.js';
import { botBranchLabel, describeBotNode } from './bot-ui.js';
import { messageModeOf, optionsForMode } from './node-forms/message-form.js';
import { t } from '../../i18n/index.js';

/**
 * @typedef {import('../../data/bots.js').BotDetail} BotDetail
 * @typedef {import('../../data/bots.js').BotNodeCounts} BotNodeCounts
 * @typedef {import('../../data/bots.js').BotActivationPreview} BotActivationPreview
 * @typedef {import('../../../../shared/bots/bot-definition.js').BotDefinition} BotDefinition
 * @typedef {import('../../../../shared/bots/bot-definition.js').BotNode} BotNode
 * @typedef {import('../../lib/bot-validation.js').BotEditorProblem} BotEditorProblem
 * @typedef {import('../../lib/bot-catalog.js').BotCatalogItem} BotCatalogItem
 * @typedef {import('../../lib/bot-editor-state.js').BotInsertPoint} BotInsertPoint
 * @typedef {import('./bot-editor-header.js').BotEditorTab} BotEditorTab
 * @typedef {{ kind: 'none' } | { kind: 'node', nodeId: string } | { kind: 'entry' } | { kind: 'catalog', point: BotInsertPoint | null } | { kind: 'test' }} EditorSelection
 * @typedef {{ kind: 'name' } | { kind: 'activation', preview: BotActivationPreview } | { kind: 'conflict', savedBy: { name?: string | null } | null, savedAt: string | null }
 *   | { kind: 'leave', to: string } | { kind: 'deleteSplit', nodeId: string } | { kind: 'dropBranches', branches: Array<{ label: string, count: number }>, apply: () => void }
 *   | { kind: 'deactivate' } | { kind: 'delete' } | null} EditorDialog
 */

const DRAFT_SAVE_DELAY_MILLISECONDS = 500;
const COALESCE_MILLISECONDS = 1000;
const NODE_COUNTS_REFRESH_SECONDS = 30;

/** @param {{ botId: string }} props */
export function BotEditorPage({ botId }) {
  const isMobile = useIsMobile();
  const isWide = useMediaQuery('(min-width: 1280px)');
  const tab = readBotEditorTab(searchParamsState.value);
  const [bot, setBot] = useState(/** @type {BotDetail | null} */ (null));
  const [loadState, setLoadState] = useState(/** @type {'loading' | 'ready' | 'error' | 'notFound'} */ ('loading'));
  const [history, setHistory] = useState(() => createBotHistory(createEmptyBotDefinition()));
  const definition = history.present;
  const definitionRef = useRef(definition);
  definitionRef.current = definition;
  const botRef = useRef(bot);
  botRef.current = bot;
  const [selection, setSelection] = useState(/** @type {EditorSelection} */ ({ kind: 'none' }));
  const [focusTarget, setFocusTarget] = useState(BOT_ENTRY_FOCUS);
  const zoomKey = `bot-${botId}`;
  const [zoom, setZoom] = useState(() => readStoredFlowZoom(zoomKey, isMobile ? FLOW_MOBILE_ZOOM : 1));
  const [showCountsPreference, setShowCountsPreference] = useState(/** @type {boolean | null} */ (null));
  const [counts, setCounts] = useState(/** @type {BotNodeCounts | null} */ (null));
  const [isDragging, setIsDragging] = useState(false);
  const [pendingItemKey, setPendingItemKey] = useState(/** @type {string | null} */ (null));
  const [announcement, setAnnouncement] = useState('');
  const [dialog, setDialog] = useState(/** @type {EditorDialog} */ (null));
  const [isSaving, setIsSaving] = useState(false);
  const [isActivating, setIsActivating] = useState(false);
  const [offeredDraft, setOfferedDraft] = useState(/** @type {import('../../lib/bot-editor-state.js').LocalBotDraft | null} */ (null));
  const [problemsOpen, setProblemsOpen] = useState(false);
  const coalesceRef = useRef(/** @type {{ key: string, at: number } | null} */ (null));
  const lookupData = useBotLookupData();
  const lookups = useBotSummaryLookups(lookupData, definition);
  const members = workspaceMembersState.value;
  const institutionName = sessionState.value?.tenant?.displayName ?? '';

  // carregar ----------------------------------------------------------------------------------------------------------
  const load = async () => {
    setLoadState('loading');
    try {
      const loaded = await getBot(botId);
      setBot(loaded);
      setHistory(createBotHistory(loaded.definition));
      setLoadState('ready');
      document.title = `${loaded.name} · ${t('bots.list.title')}`;
      const parameters = new URLSearchParams(window.location.search);
      const draft = readLocalBotDraft(botId);
      if (shouldOfferLocalBotDraft(draft, loaded)) setOfferedDraft(draft);
      const node = parameters.get('node');
      if (node && indexBotTree(loaded.definition).has(node)) {
        setSelection({ kind: 'node', nodeId: node });
        setFocusTarget(node);
        updateSearchParams({ node: null });
      }
      if (parameters.get('test') === '1') {
        setSelection({ kind: 'test' });
        updateSearchParams({ test: null });
      }
    } catch (error) {
      if (error instanceof ApiError && (error.statusCode === 404 || error.code === 'RECORD_NOT_FOUND')) setLoadState('notFound');
      else {
        setLoadState('error');
        showErrorSnackbar(error);
      }
    }
  };
  useEffect(() => {
    load();
  }, [botId]);

  const wasActivated = Boolean(bot && (bot.activatedAt || bot.hasRuns || bot.status !== 'DRAFT'));
  const showCounts = wasActivated && (showCountsPreference ?? readStoredShowCounts(true));

  useEffect(() => {
    if (!wasActivated) return undefined;
    let isCancelled = false;
    const refresh = () => {
      if (document.visibilityState !== 'visible') return;
      getBotNodeCounts(botId).then((value) => !isCancelled && setCounts(value)).catch(() => undefined);
    };
    refresh();
    const timer = window.setInterval(refresh, NODE_COUNTS_REFRESH_SECONDS * 1000);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      isCancelled = true;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [botId, wasActivated]);

  // rascunho no navegador
  const isDirty = Boolean(bot && !isSameBotDefinition(definition, bot.definition));
  const isDirtyRef = useRef(isDirty);
  isDirtyRef.current = isDirty;
  useEffect(() => {
    if (!bot || loadState !== 'ready') return undefined;
    const timer = window.setTimeout(() => {
      if (isDirty) writeLocalBotDraft(botId, { revision: bot.revision, definition, savedAt: new Date().toISOString() });
      else if (!offeredDraft) clearLocalBotDraft(botId);
    }, DRAFT_SAVE_DELAY_MILLISECONDS);
    return () => window.clearTimeout(timer);
  }, [definition, bot?.revision]);
  useEffect(() => {
    /** @param {BeforeUnloadEvent} event */
    const warn = (event) => {
      if (!isDirtyRef.current) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => {
      window.removeEventListener('beforeunload', warn);
      const current = botRef.current;
      if (current && isDirtyRef.current) writeLocalBotDraft(botId, { revision: current.revision, definition: definitionRef.current, savedAt: new Date().toISOString() });
    };
  }, [botId]);

  // pendências ---------------------------------------------------------------------------------------------------------
  const validationContext = useMemo(() => buildBotValidationContext(lookupData, { botId, purpose: definition.purpose }),
    [lookupData.teams, lookupData.channels, lookupData.bots, lookupData.bookingPages, members, definition.purpose]);
  const problems = useMemo(() => {
    if (!bot) return [];
    const local = computeBotEditorProblems(definition, validationContext);
    return mergeBotProblems(freshBotServerProblems(bot.problems ?? [], bot.definition, definition), local);
  }, [definition, bot, validationContext]);
  const byNode = useMemo(() => botProblemsByNode(problems), [problems]);
  const stepNumbers = useMemo(() => botStepNumbers(definition), [definition]);
  const index = useMemo(() => indexBotTree(definition), [definition]);

  /** @param {string} nodeId */
  const nodeTitle = (nodeId) => {
    const node = index.get(nodeId)?.node;
    if (!node) return t('bots.nodes.UNKNOWN.name');
    return `${stepNumbers.get(nodeId) ?? ''}. ${describeBotNode(node, lookups).title}`.replace(/^\. /, '');
  };
  /** @param {string} text */
  const announce = (text) => {
    setAnnouncement('');
    requestAnimationFrame(() => setAnnouncement(text));
  };

  // mudanças no rascunho ----------------------------------------------------------------------------------------------
  /** @param {BotDefinition} next @param {string | null} [coalesceKey] */
  const commit = (next, coalesceKey = null) => {
    const now = Date.now();
    const previous = coalesceRef.current;
    const coalesce = Boolean(coalesceKey && previous && previous.key === coalesceKey && now - previous.at < COALESCE_MILLISECONDS);
    coalesceRef.current = coalesceKey ? { key: coalesceKey, at: now } : null;
    setHistory((value) => (coalesce ? { ...value, present: next } : pushBotHistory(value, next)));
  };
  const undo = () => {
    coalesceRef.current = null;
    setHistory((value) => undoBotHistory(value));
    announce(t('bots.editor.undone'));
  };
  const redo = () => {
    coalesceRef.current = null;
    setHistory((value) => redoBotHistory(value));
    announce(t('bots.editor.redone'));
  };

  // painel e abas ------------------------------------------------------------------------------------------------------
  /** @param {BotEditorTab} next */
  const setTab = (next) => {
    updateSearchParams({ tab: next === 'editor' ? null : next });
    if (next !== 'editor') setSelection({ kind: 'none' });
  };
  /** @param {EditorSelection} next */
  const select = (next) => {
    setSelection(next);
    if (tab !== 'editor') updateSearchParams({ tab: null });
  };
  const closePanel = () => {
    if (tab !== 'editor') updateSearchParams({ tab: null });
    setSelection({ kind: 'none' });
    setPendingItemKey(null);
  };
  /** @param {string} nodeId */
  const goToNode = (nodeId) => {
    if (!index.has(nodeId)) return;
    select({ kind: 'node', nodeId });
    setFocusTarget(nodeId);
    requestAnimationFrame(() => document.querySelector(`[data-focus-id="${CSS.escape(nodeId)}"]`)?.scrollIntoView({ block: 'center', inline: 'center', behavior: 'smooth' }));
  };

  // inserir ------------------------------------------------------------------------------------------------------------
  /** @param {string} reason */
  const refusalText = (reason) => t(`bots.canvas.refusals.${reason}`, { maximum: reason === 'LIMIT_NODES' ? MAXIMUM_BOT_NODES : MAXIMUM_BOT_SPLIT_DEPTH });
  /** @param {BotInsertPoint} point @param {string} itemKey */
  const insertAt = (point, itemKey) => {
    const item = botCatalogItem(itemKey);
    if (!item) return;
    const check = canInsertBotNode(definition, point, item);
    if (!check.allowed) {
      enqueueSnackbar({ message: refusalText(/** @type {{ reason: string }} */ (/** @type {unknown} */ (check)).reason), variant: 'warning' });
      return;
    }
    const node = createBotNodeFromCatalog(item, { translate: t });
    const result = insertBotNode(definition, point, node);
    commit(result.definition);
    setPendingItemKey(null);
    select({ kind: 'node', nodeId: node.id });
    setFocusTarget(node.id);
    announce(t('bots.editor.inserted', { name: t(item.nameKey) }));
    if (result.movedToBranch) {
      enqueueSnackbar({
        message: t('bots.editor.movedToBranch', { branch: botBranchLabel(node, result.movedToBranch), count: result.movedCount }),
        variant: 'info', buttonLabel: t('bots.editor.undo'), onButtonClick: undo,
      });
    }
  };
  /** @param {BotInsertPoint} point */
  const openCatalogAt = (point) => {
    if (pendingItemKey) {
      insertAt(point, pendingItemKey);
      return;
    }
    select({ kind: 'catalog', point });
  };
  /** @param {BotCatalogItem} item @param {boolean} viaKeyboard */
  const pickCatalogItem = (item, viaKeyboard) => {
    const point = selection.kind === 'catalog' ? selection.point : null;
    if (point) {
      insertAt(point, item.key);
      return;
    }
    if (viaKeyboard) {
      const target = focusTarget !== BOT_ENTRY_FOCUS ? insertPointAfterBotTarget(definition, focusTarget) : null;
      insertAt(target ?? { parentId: null, branchKey: null, index: definition.nodes.length }, item.key);
      return;
    }
    setPendingItemKey(item.key);
    announce(t('bots.canvas.choosePoint'));
    enqueueSnackbar({ message: t('bots.canvas.choosePoint'), variant: 'info', dedupeKey: 'bot-choose-point' });
  };
  /** @param {BotInsertPoint} point */
  const insertDisabledReason = (point) => {
    if (countBotNodes(definition) >= MAXIMUM_BOT_NODES) return refusalText('LIMIT_NODES');
    if (pendingItemKey) {
      const item = botCatalogItem(pendingItemKey);
      const check = item ? canInsertBotNode(definition, point, item) : { allowed: true };
      if (!check.allowed) return refusalText(/** @type {{ reason: string }} */ (/** @type {unknown} */ (check)).reason);
    }
    return null;
  };
  /** @param {BotCatalogItem} item */
  const catalogDisabledReason = (item) => {
    if (countBotNodes(definition) >= MAXIMUM_BOT_NODES) return refusalText('LIMIT_NODES');
    const point = selection.kind === 'catalog' ? selection.point : null;
    if (point) {
      const check = canInsertBotNode(definition, point, item);
      if (!check.allowed) return refusalText(/** @type {{ reason: string }} */ (/** @type {unknown} */ (check)).reason);
    }
    return null;
  };

  // excluir ------------------------------------------------------------------------------------------------------------
  /** @param {string} nodeId @param {string | null} [keepBranch] */
  const removeNode = (nodeId, keepBranch = null) => {
    const location = findBotNodeLocation(definition, nodeId);
    const references = gotoNodesReferencing(definition, nodeId);
    const next = removeBotNode(definition, nodeId, { keepBranch });
    commit(next);
    setSelection({ kind: 'none' });
    const nextIndex = indexBotTree(next);
    const previous = location && location.index > 0
      ? [...nextIndex.values()].find((entry) => entry.parentId === location.parentId && entry.branchKey === location.branchKey && entry.index === location.index - 1)?.node.id
      : location?.parentId ?? null;
    setFocusTarget(previous ?? BOT_ENTRY_FOCUS);
    announce(t('bots.editor.deleted'));
    enqueueSnackbar({
      message: references.length > 0 ? t('bots.editor.deletedWithGoto', { count: references.length }) : t('bots.editor.deleted'),
      variant: references.length > 0 ? 'warning' : 'default', buttonLabel: t('bots.editor.undo'), onButtonClick: undo,
    });
  };
  /** @param {string} nodeId */
  const requestDelete = (nodeId) => {
    const node = index.get(nodeId)?.node;
    if (!node) return;
    if (isBotSplitNode(node) && botBranchSizes(definition, nodeId).total > 0) {
      setDialog({ kind: 'deleteSplit', nodeId });
      return;
    }
    removeNode(nodeId);
  };

  // opções da mensagem -------------------------------------------------------------------------------------------------
  /**
   * Aplica uma configuração nova; se ela tira ramos com blocos, pergunta antes.
   * @param {BotNode} node @param {Record<string, any>} nextConfig @param {() => void} apply
   */
  const confirmBranchChange = (node, nextConfig, apply) => {
    const dropped = droppedBranches(node, nextConfig);
    if (dropped.length === 0) {
      apply();
      return;
    }
    setDialog({ kind: 'dropBranches', branches: dropped.map((entry) => ({ label: botBranchLabel(node, entry.branchKey), count: entry.count })), apply });
  };
  /** @param {BotNode} node @param {'NONE' | 'BUTTONS' | 'LIST' | 'DYNAMIC'} mode */
  const changeMessageMode = (node, mode) => {
    if (messageModeOf(node.config) === mode) return;
    const nextConfig = { ...node.config, options: optionsForMode(node.config, mode, () => crypto.randomUUID()) };
    confirmBranchChange(node, nextConfig, () => commit(updateBotNode(definitionRef.current, node.id, { replaceConfig: nextConfig })));
  };
  /** @param {BotNode} node @param {string} optionId */
  const removeOption = (node, optionId) => {
    const options = node.config?.options ?? {};
    const nextConfig = { ...node.config, options: { ...options, items: (options.items ?? []).filter((/** @type {any} */ item) => item.id !== optionId) } };
    confirmBranchChange(node, nextConfig, () => commit(removeStaticOption(definitionRef.current, node.id, optionId)));
  };

  // salvar -------------------------------------------------------------------------------------------------------------
  /** @returns {Promise<boolean>} */
  const save = async () => {
    const current = botRef.current;
    if (!current) return false;
    if (current.status === 'ACTIVE' && blockingBotProblems(problems).length > 0) {
      setProblemsOpen(true);
      announce(t('bots.editor.problems', { count: problems.length }));
      return false;
    }
    const sent = definitionRef.current;
    setIsSaving(true);
    try {
      const saved = await saveBotDefinition(current.id, { definition: sent, expectedRevision: current.revision });
      setBot(saved);
      if (isSameBotDefinition(definitionRef.current, sent)) setHistory((value) => ({ ...value, present: saved.definition }));
      clearLocalBotDraft(current.id);
      setOfferedDraft(null);
      showSuccessSnackbar(t('bots.editor.saved'));
      announce(t('bots.editor.saved'));
      return true;
    } catch (error) {
      if (error instanceof ApiError && error.code === 'EDIT_CONFLICT') {
        setDialog({ kind: 'conflict', savedBy: error.details?.savedBy ?? null, savedAt: error.details?.savedAt ?? null });
      } else if (error instanceof ApiError && error.code === 'BOT_INVALID' && Array.isArray(error.details?.problems)) {
        setBot((value) => (value ? { ...value, problems: error.details?.problems } : value));
        setProblemsOpen(true);
        showErrorSnackbar(error);
      } else showErrorSnackbar(error);
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const openActivationReview = async () => {
    const latest = botRef.current;
    if (!latest) return;
    try {
      const preview = await previewBotActivation(latest.id, { expectedRevision: latest.revision });
      setDialog({ kind: 'activation', preview });
    } catch (error) {
      if (error instanceof ApiError && error.code === 'EDIT_CONFLICT') setDialog({ kind: 'conflict', savedBy: error.details?.savedBy ?? null, savedAt: error.details?.savedAt ?? null });
      else showErrorSnackbar(error);
    }
  };
  const saveAndActivate = async () => {
    const current = botRef.current;
    if (!current) return;
    const blocking = blockingBotProblems(problems);
    if (blocking.length > 0) {
      setProblemsOpen(true);
      announce(t('bots.editor.problems', { count: blocking.length }));
      return;
    }
    if (isDirtyRef.current && !(await save())) return;
    await openActivationReview();
  };
  const activate = async () => {
    const current = botRef.current;
    if (!current) return;
    setIsActivating(true);
    try {
      const activated = await activateBot(current.id, { expectedRevision: current.revision });
      setBot(activated);
      setHistory((value) => ({ ...value, present: activated.definition }));
      setDialog(null);
      setShowCountsPreference(true);
      showSuccessSnackbar(t('bots.editor.activated'));
      announce(t('bots.editor.activated'));
    } catch (error) {
      if (error instanceof ApiError && error.code === 'EDIT_CONFLICT') {
        setDialog({ kind: 'conflict', savedBy: error.details?.savedBy ?? null, savedAt: error.details?.savedAt ?? null });
        return;
      }
      if (error instanceof ApiError && error.code === 'BOT_TRIGGER_CONFLICT') {
        // outro bot ativou o mesmo gatilho depois da revisão: a revisão volta com o conflito
        showErrorSnackbar(error);
        await openActivationReview();
        return;
      }
      if (error instanceof ApiError && error.code === 'BOT_INVALID' && Array.isArray(error.details?.problems)) {
        setBot((value) => (value ? { ...value, problems: error.details?.problems } : value));
        setDialog(null);
        setProblemsOpen(true);
      }
      showErrorSnackbar(error);
    } finally {
      setIsActivating(false);
    }
  };

  const reloadFromServer = async () => {
    clearLocalBotDraft(botId);
    setOfferedDraft(null);
    setDialog(null);
    await load();
  };
  const copyWithMine = async () => {
    const current = botRef.current;
    if (!current) return;
    setIsSaving(true);
    try {
      const copy = await createBot({ name: t('bots.conflict.copyName', { name: current.name }), duplicateOf: current.id });
      await saveBotDefinition(copy.id, { definition: definitionRef.current, expectedRevision: copy.revision });
      clearLocalBotDraft(current.id);
      isDirtyRef.current = false;
      setDialog(null);
      navigate(botEditorHref(copy.id));
    } catch (error) {
      showErrorSnackbar(error);
    } finally {
      setIsSaving(false);
    }
  };
  /** @param {string} to */
  const leaveTo = (to) => {
    if (isDirtyRef.current) setDialog({ kind: 'leave', to });
    else navigate(to);
  };

  // atalhos do editor: salvar, desfazer e refazer
  useEffect(() => {
    /** @param {KeyboardEvent} event */
    const handleKeyDown = (event) => {
      const hasModifier = event.ctrlKey || event.metaKey;
      if (!hasModifier || event.altKey) return;
      const key = event.key.toLowerCase();
      if (key === 's') {
        event.preventDefault();
        if (!dialog) save();
        return;
      }
      if (dialog || isTypingTarget(event.target)) return;
      if (key === 'z' && !event.shiftKey) {
        event.preventDefault();
        undo();
      } else if ((key === 'z' && event.shiftKey) || key === 'y') {
        event.preventDefault();
        redo();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  });

  // estados da página --------------------------------------------------------------------------------------------------
  if (loadState === 'notFound' || loadState === 'error') {
    return html`<${PageCard} className="bots-page" header=${html`<${PageHeader} icon="IconRobot" title=${t('bots.list.title')} />`}>
      <div class="flows-state"><${EmptyState} icon=${loadState === 'notFound' ? 'IconRobot' : 'IconAlertTriangle'}
        title=${loadState === 'notFound' ? t('bots.editor.notFound') : t('bots.editor.loadError')}
        action=${loadState === 'notFound'
          ? html`<${Button} title=${t('bots.editor.backToList')} variant="secondary" size="small" onClick=${() => navigate(BOT_LIST_PATH)} />`
          : html`<${Button} title=${t('bots.common.retry')} variant="secondary" size="small" onClick=${load} />`} /></div>
    <//>`;
  }
  if (!bot || loadState === 'loading') return html`<${BotEditorSkeleton} />`;

  const isActive = bot.status === 'ACTIVE';
  const selectedNode = selection.kind === 'node' ? index.get(selection.nodeId)?.node ?? null : null;
  const catalogPoint = selection.kind === 'catalog' ? selection.point : null;
  /** @type {any} */
  let panel = null;
  if (tab === 'settings') {
    panel = html`<${BotSettingsPanel} definition=${definition} priority=${bot.priority} description=${bot.description} lookupData=${lookupData} problems=${problems}
      onChange=${(/** @type {BotDefinition} */ next) => commit(next, 'settings')} onClose=${closePanel}
      onDetailsChange=${async (/** @type {{ priority?: number, description?: string | null }} */ patch) => {
        try {
          const updated = await updateBotDetails(bot.id, patch);
          setBot((value) => (value ? { ...value, priority: updated.priority, description: updated.description } : value));
        } catch (error) {
          showErrorSnackbar(error);
        }
      }} />`;
  } else if (selection.kind === 'test') {
    panel = html`<${BotTestPanel} definition=${definition} botId=${bot.id} botName=${bot.name} lookupData=${lookupData} lookups=${lookups}
      institutionName=${institutionName} onClose=${closePanel} onShowNode=${(/** @type {string} */ nodeId) => {
        setFocusTarget(nodeId);
        requestAnimationFrame(() => document.querySelector(`[data-focus-id="${CSS.escape(nodeId)}"]`)?.scrollIntoView({ block: 'center', inline: 'center', behavior: 'smooth' }));
      }} />`;
  } else if (selection.kind === 'entry') {
    panel = html`<${BotTriggersPanel} definition=${definition} lookupData=${lookupData} problems=${problems.filter((problem) => problem.area === 'TRIGGERS')}
      onClose=${closePanel} onChange=${(/** @type {BotDefinition['triggers']} */ triggers) => commit({ ...definition, triggers }, 'triggers')} />`;
  } else if (selectedNode) {
    panel = html`<${BotNodePanel} node=${selectedNode} definition=${definition} botId=${bot.id} problems=${byNode.get(selectedNode.id) ?? []} lookupData=${lookupData}
      lookups=${lookups} settings=${definition.settings} purpose=${definition.purpose} variables=${variablesBefore(definition, selectedNode.id)}
      institutionName=${institutionName} onClose=${closePanel} onDelete=${() => requestDelete(selectedNode.id)} onGoToNode=${goToNode}
      onRequestModeChange=${(/** @type {any} */ mode) => changeMessageMode(selectedNode, mode)}
      onAddOption=${() => commit(addStaticOption(definition, selectedNode.id, { id: crypto.randomUUID(), title: t('bots.catalog.defaults.option', { number: (selectedNode.config?.options?.items?.length ?? 0) + 1 }) }))}
      onRemoveOption=${(/** @type {string} */ optionId) => removeOption(selectedNode, optionId)}
      onChange=${(/** @type {{ name?: string | null, config?: Record<string, unknown>, replaceConfig?: Record<string, unknown> }} */ patch) => {
        const fieldKey = patch.name !== undefined ? 'name' : Object.keys(patch.config ?? patch.replaceConfig ?? {}).join(',');
        commit(updateBotNode(definition, selectedNode.id, patch), `${selectedNode.id}:${fieldKey}`);
      }} />`;
  } else if (selection.kind === 'catalog') {
    panel = html`<${FlowSidePanel} title=${catalogPoint ? t('bots.catalog.insertTitle') : t('bots.catalog.title')} onClose=${closePanel} panelKey="catalog"
      testId="bot-catalog-panel" backLabel=${t('bots.panel.backToBot')}>
      <${BotBlockCatalog} insertLabel=${catalogPoint ? insertPointLabel(definition, catalogPoint, lookups) : null} disabledReason=${catalogDisabledReason}
        onPick=${pickCatalogItem} canDrag=${!isMobile} onDragStart=${() => setIsDragging(true)} onDragEnd=${() => setIsDragging(false)} />
    <//>`;
  }

  const menuItems = [
    ...(isActive ? [{ key: 'deactivate', text: t('bots.list.menu.deactivate'), icon: 'IconPlayerPause', onSelect: () => setDialog({ kind: 'deactivate' }) }] : []),
    {
      key: 'duplicate', text: t('bots.list.menu.duplicate'), icon: 'IconCopy',
      onSelect: async () => {
        try {
          const copy = await duplicateBot(bot.id);
          if (isDirtyRef.current) enqueueSnackbar({ message: t('bots.editor.duplicatedSaved'), variant: 'info' });
          leaveTo(botEditorHref(copy.id));
        } catch (error) {
          showErrorSnackbar(error);
        }
      },
    },
    ...(wasActivated ? [{ key: 'stats', text: t('bots.list.menu.stats'), icon: 'IconChartBar', onSelect: () => leaveTo(botStatsHref(bot.id)) }] : []),
    { key: 'delete', text: t('bots.list.menu.delete'), icon: 'IconTrash', accent: /** @type {const} */ ('danger'), separatorBefore: true, onSelect: () => setDialog({ kind: 'delete' }) },
  ];
  /** @param {BotEditorProblem} problem */
  const goToProblem = (problem) => {
    if (problem.nodeId && index.has(problem.nodeId)) goToNode(problem.nodeId);
    else if (problem.area === 'TRIGGERS') select({ kind: 'entry' });
    else if (problem.area === 'SETTINGS') setTab('settings');
  };
  const isPanelOpen = Boolean(panel);
  const activePointKey = catalogPoint ? insertPointKey(catalogPoint) : null;

  return html`<div class="flow-editor bot-editor" data-panel=${isPanelOpen ? (isWide && !isMobile ? 'beside' : 'over') : undefined} data-testid="bot-editor">
    <${BotEditorBars} bot=${bot} tab=${tab} problems=${problems} isDirty=${isDirty} isSaving=${isSaving} isMobile=${isMobile} problemsOpen=${problemsOpen}
      onProblemsOpenChange=${setProblemsOpen} onGoToProblem=${goToProblem} nodeTitle=${nodeTitle} onTab=${setTab}
      onBack=${() => leaveTo(BOT_LIST_PATH)} onExit=${() => leaveTo(wasActivated ? botStatsHref(bot.id) : BOT_LIST_PATH)}
      onStats=${wasActivated ? () => leaveTo(botStatsHref(bot.id)) : null} onRename=${() => setDialog({ kind: 'name' })}
      onTest=${() => (selection.kind === 'test' ? closePanel() : select({ kind: 'test' }))} isTesting=${selection.kind === 'test'}
      onSave=${() => save()} onSaveAndActivate=${saveAndActivate} menuItems=${menuItems} />
    ${offeredDraft
      ? html`<${BotDraftBanner}
          onRestore=${() => {
            setHistory((value) => pushBotHistory(value, offeredDraft.definition));
            setOfferedDraft(null);
          }}
          onDiscard=${() => {
            clearLocalBotDraft(bot.id);
            setOfferedDraft(null);
          }} />`
      : null}
    <div class="flow-editor-workspace">
      <${BotCanvas} definition=${definition} lookups=${lookups} lookupData=${lookupData} problems=${problems} problemsByNode=${byNode} stepNumbers=${stepNumbers}
        selectedNodeId=${selectedNode?.id ?? null} focusTarget=${focusTarget} onFocusTarget=${setFocusTarget} counts=${counts} showCounts=${showCounts}
        canShowCounts=${wasActivated} ariaLabel=${t('bots.canvas.treeLabel', { name: bot.name })} className="flow-editor-canvas" announcement=${announcement}
        zoom=${zoom} onZoom=${(/** @type {number} */ value) => {
          const next = normalizeFlowZoom(value);
          setZoom(next);
          storeFlowZoom(zoomKey, next);
        }}
        onToggleCounts=${() => {
          setShowCountsPreference(!showCounts);
          storeShowCounts(!showCounts);
        }}
        activePointKey=${activePointKey} isDragging=${isDragging} isPulsing=${Boolean(pendingItemKey)} insertDisabledReason=${insertDisabledReason}
        onInsert=${openCatalogAt}
        onDropAction=${(/** @type {BotInsertPoint} */ point, /** @type {string} */ itemKey) => {
          setIsDragging(false);
          insertAt(point, itemKey);
        }}
        onSelectNode=${(/** @type {string} */ nodeId) => {
          select({ kind: 'node', nodeId });
          setFocusTarget(nodeId);
        }}
        onDeleteNode=${requestDelete}
        onOpenActions=${() => select({ kind: 'catalog', point: null })}
        onActivateTarget=${(/** @type {string} */ target) => {
          if (target === BOT_ENTRY_FOCUS) select({ kind: 'entry' });
          else if (parseEmptyListFocus(target)) {
            const point = insertPointAfterBotTarget(definition, target);
            if (point) openCatalogAt(point);
          } else if (index.has(target)) select({ kind: 'node', nodeId: target });
        }}
        onInsertAfterTarget=${(/** @type {string} */ target) => {
          const point = insertPointAfterBotTarget(definition, target);
          if (point) openCatalogAt(point);
        }}
        onDeleteTarget=${(/** @type {string} */ target) => index.has(target) && requestDelete(target)}
        onEscape=${() => (isPanelOpen ? closePanel() : setSelection({ kind: 'none' }))}
        onEditEntry=${() => select({ kind: 'entry' })} isEntrySelected=${selection.kind === 'entry'} />
      ${panel}
    </div>
    ${isMobile && isDirty
      ? html`<div class="flow-editor-floating-save"><${Button} title=${isActive ? t('bots.editor.saveChanges') : t('bots.editor.save')} icon="IconDeviceFloppy"
          accent="blue" isLoading=${isSaving} onClick=${() => save()} /></div>`
      : null}
    ${renderDialog()}
  </div>`;

  function renderDialog() {
    if (!dialog || !bot) return null;
    const close = () => setDialog(null);
    switch (dialog.kind) {
      case 'name':
        return html`<${BotNameDialog} mode="edit" initialName=${bot.name} existingNames=${(lookupData.bots ?? []).filter((item) => item.id !== bot.id).map((item) => item.name)}
          onClose=${close} onSubmit=${async (/** @type {{ name: string }} */ value) => {
            try {
              const updated = await updateBotDetails(bot.id, { name: value.name });
              setBot((current) => (current ? { ...current, name: updated.name } : current));
              document.title = `${updated.name} · ${t('bots.list.title')}`;
              close();
              return true;
            } catch (error) {
              showErrorSnackbar(error);
              return false;
            }
          }} />`;
      case 'activation':
        return html`<${BotActivationModal} preview=${dialog.preview} definition=${definition} lookupData=${lookupData} isBusy=${isActivating} onClose=${close}
          onActivate=${activate} onEditProblems=${() => {
            close();
            setProblemsOpen(true);
          }} />`;
      case 'conflict':
        return html`<${BotConflictModal} savedBy=${dialog.savedBy} savedAt=${dialog.savedAt} isBusy=${isSaving} onClose=${close} onReload=${reloadFromServer} onCopy=${copyWithMine} />`;
      case 'leave':
        return html`<${BotLeaveModal} canSave=${!(isActive && blockingBotProblems(problems).length > 0)} isBusy=${isSaving} onClose=${close}
          onLeave=${() => {
            clearLocalBotDraft(bot.id);
            isDirtyRef.current = false;
            const to = dialog.to;
            close();
            navigate(to);
          }}
          onSaveAndLeave=${async () => {
            const to = dialog.to;
            if (await save()) {
              isDirtyRef.current = false;
              close();
              navigate(to);
            }
          }} />`;
      case 'deleteSplit': {
        const node = index.get(dialog.nodeId)?.node;
        if (!node) return null;
        const sizes = botBranchSizes(definition, node.id);
        return html`<${BotDeleteSplitModal} total=${sizes.total}
          branches=${botBranchKeysOf(node).map((key) => ({ key, label: botBranchLabel(node, key), count: sizes.byBranch[key] ?? 0 }))}
          onClose=${close} onConfirm=${(/** @type {string | null} */ keepBranch) => {
            close();
            removeNode(node.id, keepBranch);
          }} />`;
      }
      case 'dropBranches':
        return html`<${BotDropBranchesConfirm} branches=${dialog.branches} onClose=${close} onConfirm=${() => {
          const apply = dialog.apply;
          close();
          apply();
        }} />`;
      case 'deactivate':
        return html`<${BotLifecycleConfirm} kind="deactivate" name=${bot.name} isBusy=${isActivating} onClose=${close} onConfirm=${async () => {
          setIsActivating(true);
          try {
            const result = await deactivateBot(bot.id);
            setBot((current) => (current ? { ...result.bot, definition: current.definition } : current));
            showSuccessSnackbar(t('bots.lifecycle.deactivate.done', { count: result.stoppedRuns }));
            close();
          } catch (error) {
            showErrorSnackbar(error);
          } finally {
            setIsActivating(false);
          }
        }} />`;
      case 'delete':
        return html`<${BotLifecycleConfirm} kind="delete" name=${bot.name} isBusy=${isActivating} onClose=${close} onConfirm=${async () => {
          setIsActivating(true);
          try {
            await deleteBot(bot.id);
            clearLocalBotDraft(bot.id);
            isDirtyRef.current = false;
            close();
            showSuccessSnackbar(t('bots.lifecycle.delete.done'));
            navigate(BOT_LIST_PATH);
          } catch (error) {
            showErrorSnackbar(error);
          } finally {
            setIsActivating(false);
          }
        }} />`;
      default:
        return null;
    }
  }
}

/**
 * "Depois de Mensagem", "No começo do ramo Sim" ou "No começo do bot".
 * @param {BotDefinition} definition @param {BotInsertPoint} point @param {import('../../../../shared/bots/bot-summary.js').BotSummaryLookups} lookups
 */
export function insertPointLabel(definition, point, lookups) {
  const index = indexBotTree(definition);
  const owner = point.parentId ? index.get(point.parentId)?.node ?? null : null;
  const list = owner ? owner.branches?.[point.branchKey ?? ''] ?? [] : definition.nodes;
  const previous = point.index > 0 ? list[point.index - 1] : null;
  if (previous) return t('bots.catalog.after', { name: describeBotNode(previous, lookups).title });
  if (owner && point.branchKey) return t('bots.catalog.branchStart', { branch: botBranchLabel(owner, point.branchKey) });
  return t('bots.catalog.botStart');
}

/** Canvas esqueleto enquanto o bot carrega. */
function BotEditorSkeleton() {
  return html`<div class="flow-editor bot-editor" aria-busy="true" data-testid="bot-editor-loading">
    <div class="flow-editor-bar flow-editor-bar-top"><${Skeleton} width=${220} height=${20} /></div>
    <div class="flow-editor-bar flow-editor-bar-tabs"><${Skeleton} width=${260} height=${20} /></div>
    <div class="flow-editor-workspace">
      <div class="flow-canvas"><div class="flow-canvas-viewport"><div class="flow-canvas-content"><div class="flow-row">
        <div class="flow-entry-card flow-skeleton-card"><${Skeleton} width="60%" height=${16} /><${Skeleton} height=${64} /></div>
        ${[0, 1, 2].map((key) => html`<div key=${key} class="flow-skeleton-step"><span class="flow-connector" aria-hidden="true"></span>
          <div class="flow-node-card flow-skeleton-card"><${Skeleton} width=${32} height=${32} radius=${16} /><${Skeleton} width="70%" height=${14} /></div></div>`)}
      </div></div></div></div>
    </div>
  </div>`;
}
